import { Box, Divider, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { StaffCersPrePostQuery } from "generated-graphql/graphql";
import { ReactNode } from "react";

import { StatusMetricCard } from "../StatusMetricCard";
import { formatDelta, formatPct } from "./PrePostTable";

const SECTION_LABEL_SX = {
  mb: 1.5,
  display: "block",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  fontSize: "0.7rem",
} as const;

/** One titled cluster of StatusMetricCard cards (local copy of the rejections-tracker layout). */
function StatSection({
  label,
  color,
  columns,
  children,
}: {
  label: string;
  color: string;
  columns: { xs: number; lg: number };
  children: ReactNode;
}) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color={color} sx={SECTION_LABEL_SX}>
        {label}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: `repeat(${columns.xs}, minmax(0, 1fr))`,
            lg: `repeat(${columns.lg}, minmax(0, 1fr))`,
          },
          gap: 1.5,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

const ofPct = (part: number, total: number, pct: number | null | undefined) =>
  `${part} of ${total}${pct == null ? "" : ` (${formatPct(pct)})`}`;

/** StatusMetricCard wrapped in an explanatory hover tooltip (every stat here needs one). */
function TipMetric({
  name,
  value,
  loading,
  tip,
}: {
  name: string;
  value: number | string | undefined;
  loading: boolean;
  tip: string;
}) {
  return (
    <Tooltip title={tip}>
      <Box sx={{ minWidth: 0 }}>
        <StatusMetricCard name={name} value={value} loading={loading} />
      </Box>
    </Tooltip>
  );
}

/**
 * Dashboard cards above the pre/post tables: the facility-weighted comparison
 * plus link coverage (the fuzzy-join disclosure numbers). Unlike the
 * rejections-tracker cards these are display-only — the comparison has no
 * drill-in target that corresponds.
 */
export function PrePostStatCards({
  result,
  loading,
}: {
  result: StaffCersPrePostQuery["staffCersPrePost"] | undefined;
  loading: boolean;
}) {
  const fw = result?.facilityWeighted;
  const matchRate = result?.matchRate;

  return (
    <Paper elevation={0} sx={{ px: 3, py: 2.5 }}>
      <Stack direction="column" spacing={2.5} sx={{ minWidth: 0 }}>
        <Box sx={{ minWidth: 0 }}>
          <StatSection
            label="Facility-Weighted Comparison"
            color="primary.main"
            columns={{ xs: 2, lg: 5 }}
          >
            <TipMetric
              name="Facilities analyzed"
              value={fw?.facilitiesAnalyzed}
              loading={loading}
              tip="Matched, non-ignored CERS facilities with at least one reviewed submittal both before and after their cutoff (their first verified FILING_COMPLETE Encamp report)."
            />
            <TipMetric
              name="Avg prior rate"
              value={fw ? formatPct(fw.avgPriorRatePct) : undefined}
              loading={loading}
              tip="Average of each eligible facility's pre-cutoff rejection rate (rejected submittals ÷ decided submittals before Encamp's first verified filing). Every facility counts equally, regardless of volume."
            />
            <TipMetric
              name="Avg after rate"
              value={fw ? formatPct(fw.avgAfterRatePct) : undefined}
              loading={loading}
              tip="Average of each eligible facility's post-cutoff rejection rate (rejected submittals ÷ decided submittals on or after Encamp's first verified filing). Every facility counts equally, regardless of volume."
            />
            <TipMetric
              name="Avg delta"
              value={fw ? formatDelta(fw.avgDeltaPct) : undefined}
              loading={loading}
              tip="Average after-minus-prior percentage-point change across facilities. Each facility has equal weight; negative means the typical facility improved. This can differ from the pooled row, which weights facilities by submittal volume."
            />
            <TipMetric
              name="Improved / worsened / unchanged"
              value={fw ? `${fw.improved} / ${fw.worsened} / ${fw.unchanged}` : undefined}
              loading={loading}
              tip="How many eligible facilities' rejection rates went down, went up, or stayed the same after their cutoff."
            />
          </StatSection>
        </Box>

        <Divider />

        <Box sx={{ minWidth: 0 }}>
          <StatSection label="Link Coverage" color="info.main" columns={{ xs: 2, lg: 3 }}>
            <TipMetric
              name="Linked submittals"
              value={
                matchRate
                  ? ofPct(matchRate.linkedSubmittals, matchRate.totalSubmittals, matchRate.pct)
                  : undefined
              }
              loading={loading}
              tip="Eligible submittals matched to the Encamp report that filed them. The match is fuzzy: the nearest verified filing within −3 to +30 days of the submittal date."
            />
            <TipMetric
              name="After bucket linked"
              value={
                matchRate
                  ? ofPct(
                      matchRate.afterLinkedSubmittals,
                      matchRate.afterTotalSubmittals,
                      matchRate.afterPct
                    )
                  : undefined
              }
              loading={loading}
              tip="Post-cutoff submittals with a linked Encamp report. Unlinked ones count as out-of-band in the attribution table below — filings we can't credit to Encamp."
            />
            <TipMetric
              name="Eligible facilities"
              value={
                result
                  ? `${fw?.facilitiesAnalyzed ?? 0} of ${result.facilitiesWithCutoff}`
                  : undefined
              }
              loading={loading}
              tip="Facilities meeting the both-sides rule, out of all matched facilities that have a cutoff at all. Facilities with activity on only one side of their cutoff can't be compared."
            />
          </StatSection>
        </Box>
      </Stack>
    </Paper>
  );
}
